import React, { useState, useLayoutEffect, useRef } from 'react';

// 按 banner 缓存测量结果，避免 remount 时先画 measuredTextWidth=0 的错误路径
const measureCache = new Map();

/**
 * SvgCurve - A customizable SVG divider component that supports both static percentage
 * layouts and dynamic auto-adaptation based on the width of a reference text element.
 * 
 * @param {object} props
 * @param {React.RefObject} props.textRef - (Optional) React ref to the text element for auto-adaptation
 * @param {string|number} props.measureKey - Cache key (e.g. banner id) so remount can reuse last measurement
 * @param {number} props.textPadding - Horizontal padding (px) after text ends before curve starts (default 24)
 * @param {number} props.curveWidth - Horizontal width (px) of the curve transition (default 120)
 * @param {number} props.width - Static virtual viewBox width (default 1200, ignored if textRef is used)
 * @param {number} props.height - Height of the SVG coordinate space (default 160)
 * @param {number} props.startY - Starting Y coordinate (left side height, default 0)
 * @param {number} props.endY - Ending Y coordinate (right side height, default 80)
 * @param {number} props.curveStartPercent - (Static) horizontal start of curve (default 25)
 * @param {number} props.curveEndPercent - (Static) horizontal end of curve (default 40)
 * @param {number} props.curveTension - Curve tension control, between 0 and 1 (default 0.5)
 * @param {string} props.fillColor - The fill color of the shape (default '#009ba1')
 * @param {string} props.className - Extra CSS classes to apply to the wrapper div
 */
export default function SvgCurve({
  textRef,
  measureKey,
  textPadding = 24,
  curveWidth = 120,
  width = 1200,
  height = 160,
  startY = 0,
  endY = 80,
  curveStartPercent = 25,
  curveEndPercent = 40,
  curveTension = 0.5,
  fillColor = '#009ba1',
  className = '',
  ...props
}) {
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState(() => {
    if (!textRef) {
      return { containerWidth: width, measuredTextWidth: 0 };
    }
    const cached = measureKey != null ? measureCache.get(measureKey) : null;
    return cached || { containerWidth: 1200, measuredTextWidth: 0 };
  });

  // 绘制前同步测量；不要用 visibility:hidden 等就绪——父级 opacity/scale 动画时会把 SVG 藏死
  useLayoutEffect(() => {
    if (!textRef) return;

    const updateDimensions = () => {
      if (!containerRef.current) return;

      // offsetWidth 不受祖先 transform(scale) 影响，避免缩放过程中路径被二次校正
      const containerWidth = containerRef.current.offsetWidth || 1200;
      const measuredTextWidth = textRef.current ? textRef.current.offsetWidth : 0;
      const next = { containerWidth, measuredTextWidth };

      if (measureKey != null) {
        measureCache.set(measureKey, next);
      }

      setDimensions((prev) => {
        if (
          prev.containerWidth === containerWidth &&
          prev.measuredTextWidth === measuredTextWidth
        ) {
          return prev;
        }
        return next;
      });
    };

    updateDimensions();

    let resizeObserver = null;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        updateDimensions();
      });
      if (containerRef.current) {
        resizeObserver.observe(containerRef.current);
      }
      if (textRef.current) {
        resizeObserver.observe(textRef.current);
      }
    }

    window.addEventListener('resize', updateDimensions);

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      window.removeEventListener('resize', updateDimensions);
    };
  }, [textRef, measureKey]);

  let finalWidth = width;
  let curveStartX = 0;
  let curveEndX = 0;

  if (textRef) {
    finalWidth = dimensions.containerWidth;
    curveStartX = Math.min(
      dimensions.measuredTextWidth + textPadding,
      finalWidth - curveWidth
    );
    curveEndX = Math.min(curveStartX + curveWidth, finalWidth);
  } else {
    curveStartX = (width * curveStartPercent) / 100;
    curveEndX = (width * curveEndPercent) / 100;
  }

  const dx = curveEndX - curveStartX;
  const cp1x = curveStartX + dx * curveTension;
  const cp1y = startY;
  const cp2x = curveEndX - dx * curveTension;
  const cp2y = endY;

  const pathData = [
    `M 0,${height}`,
    `L 0,${startY}`,
    `L ${curveStartX},${startY}`,
    `C ${cp1x},${cp1y} ${cp2x},${cp2y} ${curveEndX},${endY}`,
    `L ${finalWidth},${endY}`,
    `L ${finalWidth},${height}`,
    'Z'
  ].join(' ');

  return (
    <div 
      ref={containerRef} 
      className={className} 
      style={{ width: '100%', position: 'relative', overflow: 'hidden' }}
      {...props}
    >
      <svg
        viewBox={`0 0 ${finalWidth} ${height}`}
        width="100%"
        height={height}
        preserveAspectRatio="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{ display: 'block' }}
      >
        <path d={pathData} fill={fillColor} />
      </svg>
    </div>
  );
}
